import React, { useState, useCallback, useMemo, useRef, useEffect, useContext } from 'react';
import { Button, Input, InputGroup, InputGroupText, Card, CardBody } from 'src/components/ui';
import { concatenatePaths, getObject, mapObject, invertMap } from '../../../util/mapObject';
import { useSideChannelSubscription } from '../../../util/useSideChannel';
import Notification from '../../Notification';
import getPathFromId from '../../../util/getPathFromId';
import { HEADER_COMPONENTS } from '../headers';
import { useJnx } from '../../../util/jnx';

import MakeArrayItem from './MakeArrayItem';
import ArrayFieldDescription from './ArrayFieldDescription';
import ArrayFieldTitle from './ArrayFieldTitle';
import useCompiledPerRowConditionals from './useCompiledPerRowConditionals';
import useOptionsLookup, { lookupOptions, getLookupCacheKey, buildParamsJnx, getResolvedLookupKey } from '../hooks/useOptionsLookup';
import parseObjectMapFromSchema from '../../../util/parseObjectMapFromSchema';
import Loader from '../../Loader';
import useTableLayoutType from './useTableLayoutType';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import Icon from 'src/components/Icon';
import { AuthContext } from '../../../context/AuthContext';
import ArrayLookupCacheContext from '../ArrayLookupCacheContext';

function ArrayFieldTemplate(props) {
  const {
    $id,
    layout,
    filterRowJnx,
    formContext,
    hasAddBtn,
    headerOnEmpty,
    hideHeader,
    searchable,
    uiSectionType,
    headers,
    itemsAreObjectType,
    itemsUiSchema,
    jnxOnAddAction,
    loading,
    errorLoading,
    objectItemsSchema,
    onEmptyMessage,
    panels,
    perRowClassNamesIf,
    perRowShowColumnsIf,
    disabled,
    readonlyRowIfJnx,
    rootFormData,
    rowClassNames,
    expandable,
    expanded,
    toggleExpand,
    hideTitle,
    additionalButtons,
    selectedItem,
    setSelectedItem,
    uiDescription,
    asCard,
    arrayLookupCache,
    itemLookupEntries,
  } = useArrayFieldTemplateHooks(props);

  const uiFilteredItems = useMemo(() => {
    return (props.items ?? []).filter(item => {
      if (filterRowJnx)
        return filterRowJnx.eval(item?.children?.props?.formData || {}, '', {
          root: rootFormData,
          formContext,
        });

      return true;
    });
  }, [props.items, filterRowJnx, rootFormData, formContext]);

  const [searchText, setSearchText] = useState('');

  // Resolves the display label(s) for a row's lookup-backed column(s) (e.g. an institution name
  // shown via LookupFormField) from the already-fetched arrayLookupCache, so search can match on
  // what the user actually sees instead of only the raw stored id in formData.
  const resolveRowLookupLabels = useCallback(
    formData => {
      if (!formData || !itemLookupEntries.length) return '';

      return itemLookupEntries
        .map(({ lookup, baseKey, propKey }) => {
          const rowId = formData[propKey];
          if (rowId == null) return '';

          const resolvedKey = arrayLookupCache.resolvedKeyByBase[baseKey];
          const options = resolvedKey && arrayLookupCache.cache[resolvedKey]?.options;
          if (!options) return '';

          const idField = lookup.id || 'id';
          const match = options.find(opt => `${getObject(opt, idField)}` === `${rowId}`);

          return match && lookup.label ? getObject(match, lookup.label) : '';
        })
        .filter(Boolean)
        .join(' ');
    },
    [itemLookupEntries, arrayLookupCache]
  );

  const filteredItems = useMemo(() => {
    if (!searchText) {
      return uiFilteredItems.map((item, idx) => [item, idx]);
    }

    const searchRE = new RegExp(searchText, 'gi');

    return (
      uiFilteredItems.reduce((_, item, idx) => {
        const formData = item?.children?.props?.formData;
        if (!formData) return _;

        let matches = false;

        try {
          const lookupLabels = resolveRowLookupLabels(formData);
          const haystack = lookupLabels ? `${JSON.stringify(formData)} ${lookupLabels}` : JSON.stringify(formData);
          matches = searchRE.test(haystack);
        } catch (e) {
          matches = true;
        }

        if (matches) {
          _.push([item, idx]);
        }

        return _;
      }, []) ?? []
    );
  }, [searchText, props.items, resolveRowLookupLabels]);

  const fieldset = (
    <fieldset className={props.className} id={props.fieldPathId.$id} style={{ display: 'block' }}>
      {!hideTitle ? (
        <ArrayFieldTitle
          key={`array-field-title-${props.fieldPathId.$id}`}
          expandable={expandable}
          expanded={expanded}
          toggleExpand={toggleExpand}
          fieldPathId={props.fieldPathId}
          sectionType={uiSectionType}
          title={props.uiSchema['ui:title'] || props.title}
          required={props.required}
          bindings={{ formContext }}
        />
      ) : null}
      {(uiDescription || props.uiSchema['ui:description'] || props.schema.description) && (
        <ArrayFieldDescription
          key={`array-field-description-${props.fieldPathId.$id}`}
          DescriptionField={props.registry?.templates?.DescriptionFieldTemplate}
          fieldPathId={props.fieldPathId}
          description={uiDescription || props.uiSchema['ui:description'] || props.schema.description}
          registry={props.registry}
        />
      )}
      {expanded ? (
        <div className="w-full" style={{ overflow: 'auto' }}>
          {searchable && uiFilteredItems && uiFilteredItems.length > 0 ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <InputGroup size="sm" className={`input-group-ghost array-filter-input-group ${searchText ? 'has-value' : ''}`}>
                <InputGroupText className="bg-transparent border-0 pl-2 pr-1 text-muted">
                  <Icon icon="Funnel" size={14} />
                </InputGroupText>
                <Input
                  type="text"
                  size="sm"
                  className="ghost-input"
                  placeholder={t`Filter rows...`}
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
              </InputGroup>
            </div>
          ) : null}
          {layout.wrapArray(
            <>
              {((uiFilteredItems && uiFilteredItems.length) || headerOnEmpty) && headers.length && !hideHeader && layout.makeHeaders
                ? layout.makeHeaders()
                : null}
              {loading ? (
                layout.wrapBody(layout.wrapLoader(<Loader dots />))
              ) : (
                <>
                  {errorLoading
                    ? layout.wrapBody(
                        <>
                          <Notification error={errorLoading} />
                        </>
                      )
                    : null}
                  {layout.wrapBody(
                    <>
                      {filteredItems?.length
                        ? filteredItems.map(([p, rowIdx]) => (
                            <MakeArrayItem
                              key={rowIdx}
                              $id={$id}
                              props={p}
                              onAddClick={jnxOnAddAction || props.onAddClick}
                              layout={layout}
                              rowIdx={rowIdx}
                              rowCount={props.items.length}
                              formContext={formContext}
                              rootFormData={rootFormData}
                              filterRowJnx={filterRowJnx}
                              readonly={disabled}
                              readonlyRowIfJnx={readonlyRowIfJnx}
                              itemsAreObjectType={itemsAreObjectType}
                              objectItemsSchema={objectItemsSchema}
                              panels={panels}
                              itemsUiSchema={itemsUiSchema}
                              showColumnsIf={perRowShowColumnsIf}
                              rowClassNames={rowClassNames}
                              rowClassNamesIf={perRowClassNamesIf}
                              additionalButtons={additionalButtons}
                            />
                          ))
                        : onEmptyMessage
                        ? layout.makeEmptyMessage()
                        : null}
                      {hasAddBtn
                        ? layout.wrapAddButton(
                            <Button
                              className="array-item-add"
                              size="sm"
                              color="info"
                              onClick={jnxOnAddAction || props.onAddClick}
                              disabled={props.disabled || props.readonly}
                            >
                              <Icon icon="Plus" weight="regular" />
                              {itemsUiSchema['ui:addButtonText'] ? ` ${itemsUiSchema['ui:addButtonText']}` : <Trans>Add Item</Trans>}
                            </Button>
                          )
                        : null}
                    </>
                  )}
                </>
              )}
            </>,
            {
              selectedItem,
              $id,
              rootFormData,
              formContext,
              setSelectedItem,
              onAddClick: jnxOnAddAction || props.onAddClick,
              props,
            }
          )}
        </div>
      ) : null}
    </fieldset>
  );

  return (
    <ArrayLookupCacheContext.Provider value={arrayLookupCache}>
      {asCard ? (
        <Card>
          <CardBody>{fieldset}</CardBody>
        </Card>
      ) : (
        fieldset
      )}
    </ArrayLookupCacheContext.Provider>
  );
}

function useArrayFieldTemplateHooks(props) {
  const {
    registry: { formContext },
    schema,
    fieldPathId: { $id },
    uiSchema = {},
    formData,
  } = props;

  const {
    'ui:showheaderOnEmpty': headerOnEmpty,
    'ui:addAction': addAction,
    'ui:expandable': expandable,
    'ui:expandedDefault': expandedDefault = true,
    'ui:onEmptyMessage': onEmptyMessage,
    'ui:arrayType': arrayType = 'table',
    'ui:onDataChanged': onDataChanged,
    'ui:hideTitle': propHideTitle,
    'ui:hideLabel': propHideLabel,
    'ui:hideHeader': hideHeader,
    'ui:descriptionIf': descriptionIf,
    'ui:searchable': searchable,
    'ui:sectionType': uiSectionType,
    'ui:asCard': asCard = false,
    'ui:disableIf': disableIf,
    items: itemsUiSchema = {},
  } = uiSchema;

  const hideTitle = useMemo(() => {
    if (propHideLabel != null) return propHideLabel;
    return propHideTitle ?? false;
  }, [propHideLabel, propHideTitle]);

  const [expanded, setExpanded] = useState(expandedDefault);
  const [selectedItem, setSelectedItem] = useState(0);

  function toggleExpand(e) {
    e.preventDefault();
    e.stopPropagation();
    setExpanded(!expanded);
  }

  const { sideChannel, openModal } = formContext;
  const rootFormData = useSideChannelSubscription(sideChannel, 0) || {};
  const { setFormDataValues } = formContext;
  const { 'ui:filterRow': filterRow, 'ui:readonlyRowIf': readonlyRowIf } = itemsUiSchema;
  const {
    items: { required: requiredColumns = [], lookup, additionalButtons },
  } = schema;

  const { options, loadingOptions, errorLoadingOptions } = useOptionsLookup({ lookup, rootFormData, formContext });

  const auth = useContext(AuthContext);

  // One lookup config per distinct item property (deduped by its static config key) — the JNX
  // instances for any `expr:` params are built ONCE here (schema-driven, changes only if the
  // schema itself changes), not re-parsed every time rootFormData changes below.
  const itemLookupEntries = useMemo(() => {
    const properties = schema?.items?.properties;
    if (!properties) return [];
    const seen = new Set();
    const entries = [];
    Object.entries(properties).forEach(([propKey, prop]) => {
      const l = prop?.lookup;
      if (!l || (!l.api && !l.entity)) return;
      const baseKey = getLookupCacheKey(l);
      if (baseKey && !seen.has(baseKey)) {
        seen.add(baseKey);
        entries.push({ lookup: l, baseKey, paramsJnx: buildParamsJnx(l.params), propKey });
      }
    });
    return entries;
  }, [schema?.items?.properties]);

  // Recomputed whenever rootFormData changes — cheap (just evaluates the already-parsed JNX
  // above), so a lookup whose params genuinely depend on form data resolves to a NEW key here,
  // which the effect below treats as "needs a fresh (single, shared) fetch." A lookup whose
  // params don't depend on form data keeps resolving to the SAME key, so it's fetched once and
  // never refetched.
  const resolvedLookupEntries = useMemo(
    () =>
      itemLookupEntries.map(({ lookup: l, baseKey, paramsJnx }) => ({
        lookup: l,
        baseKey,
        resolvedKey: getResolvedLookupKey(l, rootFormData, formContext, undefined, paramsJnx),
      })),
    [itemLookupEntries, rootFormData, formContext]
  );

  const resolvedKeyByBase = useMemo(
    () =>
      resolvedLookupEntries.reduce((_, { baseKey, resolvedKey }) => {
        if (baseKey && resolvedKey) _[baseKey] = resolvedKey;

        return _;
      }, {}),
    [resolvedLookupEntries]
  );

  const [arrayLookupCache, setArrayLookupCache] = useState({});
  // Guards against firing a second fetch for the same resolved key while the first is still in
  // flight (e.g. this effect re-running before the fetch settles) — the whole point is exactly
  // ONE network call per (lookup, resolved-params) combination, shared by every row.
  const inFlightLookupKeysRef = useRef(new Set());

  useEffect(() => {
    resolvedLookupEntries.forEach(({ lookup: l, resolvedKey }) => {
      if (!resolvedKey) return;
      if (arrayLookupCache[resolvedKey] || inFlightLookupKeysRef.current.has(resolvedKey)) return;

      inFlightLookupKeysRef.current.add(resolvedKey);
      setArrayLookupCache(prev => (prev[resolvedKey] ? prev : { ...prev, [resolvedKey]: { options: null, loading: true, error: null } }));

      // skipFilter: true — `filter` is re-applied live per row against ITS current rootFormData
      // (see useOptionsLookup's `precomputedOptions` in LookupFormField), not baked in here.
      lookupOptions(l, rootFormData, formContext, '', auth, true)
        .then(opts => {
          setArrayLookupCache(prev => ({ ...prev, [resolvedKey]: { options: opts, loading: false, error: null } }));
        })
        .catch(err => {
          setArrayLookupCache(prev => ({ ...prev, [resolvedKey]: { options: null, loading: false, error: err } }));
        })
        .finally(() => {
          inFlightLookupKeysRef.current.delete(resolvedKey);
        });
    });
  }, [resolvedLookupEntries, auth?.user?.user?.actualOrganizationalUnitId]);

  const arrayLookupCacheContextValue = useMemo(() => ({ cache: arrayLookupCache, resolvedKeyByBase }), [arrayLookupCache, resolvedKeyByBase]);

  useEffect(() => {
    if (!formData?.length && options && Array.isArray(options)) {
      const path = getPathFromId($id);
      const dataMap = parseObjectMapFromSchema(schema.items, null, { removeNullMappedFields: true });
      const invertDataMap = invertMap(dataMap);
      setFormDataValues({
        [path]: options.map(x => mapObject(x, invertDataMap)),
      });
    }
  }, [options, formData, $id, schema]);

  const arrayRoute = useMemo(() => concatenatePaths(getPathFromId($id), '..'), [$id]);

  const addItems = useCallback(
    items => {
      const path = getPathFromId($id);
      setFormDataValues({
        [path]: [...(formData || []), ...(items || [])],
      });
    },
    [$id, formData]
  );

  const jnxOnAddAction = useJnx(
    addAction,
    ([event], addActionJnx) => {
      event.stopPropagation();
      addActionJnx
        .evalAsync(rootFormData, '', {
          openModal,
          formContext,
        })
        .then(result => {
          if (Array.isArray(result) && result.length > 0) {
            addItems(result);
          }
        });
    },
    [openModal, addAction, formData, rootFormData]
  );

  const functionBinds = useMemo(
    () => ({
      set: setFormDataValues,
    }),
    [setFormDataValues]
  );

  const onDataChangedJnx = useJnx(onDataChanged, { functionBinds }, [functionBinds]);
  const onDataChangedfnRef = useRef();

  const path = useMemo(() => getPathFromId($id), [$id]);
  const formData2 = useMemo(() => getObject(rootFormData, path), [rootFormData, path]);

  onDataChangedfnRef.current = useCallback(() => {
    onDataChangedJnx.evalAsync(rootFormData, path, {
      openModal,
      formContext,
    });
  }, [$id, rootFormData, openModal, formContext]);

  useEffect(() => {
    if (onDataChangedJnx) {
      onDataChangedfnRef.current();
    }
  }, [formData2]);

  const filterRowJnx = useJnx(filterRow);
  const readonlyRowIfJnx = useJnx(readonlyRowIf);
  const disableIfJnx = useJnx(disableIf);

  const [showColumnsIf, perRowShowColumnsIf] = useCompiledPerRowConditionals(itemsUiSchema['ui:showColumnsIf']);

  const [classNamesIf, perRowClassNamesIf] = useCompiledPerRowConditionals(itemsUiSchema['ui:classNamesIf']);

  const itemsAreObjectType = schema.type === 'array' && schema.items.type === 'object' && schema.items.type && !uiSchema?.items?.['ui:field'];

  const rowClassNames = useMemo(
    () =>
      classNamesIf
        ? Object.entries(classNamesIf)
            .map(([className, conditional]) => (conditional.eval(rootFormData, arrayRoute) ? className : null))
            .filter(x => !!x)
            .join(' ')
        : '',
    [classNamesIf, rootFormData]
  );

  const rowProperties = useMemo(
    () =>
      Object.entries(schema?.items?.properties || {}).reduce(
        (_, [k, v]) => {
          const showIf = showColumnsIf[k];

          if (showIf && !showIf.eval(rootFormData, arrayRoute, { formContext: formContext })) {
            return _;
          }

          if (itemsUiSchema[k] && itemsUiSchema[k]['ui:newRow']) {
            _.panels.push({ field: k, schema: v });
          } else {
            _.primary[k] = v;
          }

          return _;
        },
        {
          primary: {},
          panels: [],
        }
      ),
    [schema?.items?.properties, rootFormData, formContext]
  );

  const headers = useMemo(
    () =>
      Object.entries(rowProperties.primary || {}).map(([k, v]) => {
        const { 'ui:noColumnTitle': noColumnTitle, 'ui:columnHeader': columnHeader, 'ui:includeToggleAll': includeToggleAll } =
          itemsUiSchema[k] || {};
        // Only boolean columns (checkboxes and the like) can offer a select-all/deselect-all toggle.
        const toggleAll = !!includeToggleAll && v?.type === 'boolean';

        if (columnHeader) {
          const Component = HEADER_COMPONENTS[columnHeader];
          if (Component) return { field: k, Component, toggleAll };
        }

        if (noColumnTitle) return { field: k, text: '', toggleAll };

        const required = requiredColumns.indexOf(k) >= 0;

        return { field: k, text: `${v?.title || k}${required ? '*' : ''}`, toggleAll };
      }),
    [rowProperties.primary]
  );

  const objectItemsSchema = useMemo(
    () =>
      !itemsAreObjectType
        ? {}
        : {
            ...(schema.items || {}),
            properties: Object.entries(rowProperties.primary).reduce((_, [k, v]) => {
              v = { ...v };

              if (!(itemsUiSchema[k] || {})['ui:noColumnTitle'] && arrayType === 'table') {
                v.title = ' ';
              }

              _[k] = v;

              return _;
            }, {}),
          },
    [itemsAreObjectType, rowProperties.primary, arrayType]
  );

  const panels = rowProperties.panels;

  const disabled = !!disableIfJnx?.eval(rootFormData, path, { formContext });

  const hasAddBtn = !disabled && props.canAdd && itemsUiSchema['ui:addable'] !== false;
  const hasAddBtnInRow = !disabled && props.canAdd && itemsUiSchema['ui:addableFromRow'] === true;
  const {
    'ui:tabNameExpr': tabNameExpr,
    'ui:tabsWrap': tabsWrap,
    'ui:orderable': orderable,
    'ui:removable': removable,
    'ui:positionButtons': positionButtons,
  } = itemsUiSchema || {};
  const hasMoveBtns = !disabled && orderable !== false && (props.hasMoveUp || props.hasMoveDown);
  const hasRemoveBtn = removable !== false && props.hasRemove;
  const hasToolbar = (props.items[0] || {}).hasToolbar && (hasMoveBtns || hasRemoveBtn);

  const descriptionIfJnx = useJnx(descriptionIf);
  const uiDescription = useMemo(() => {
    if (!descriptionIfJnx) return '';

    return descriptionIfJnx.eval(rootFormData, path, { formContext });
  }, [descriptionIfJnx, rootFormData, formContext]);

  const layout = useTableLayoutType({
    $id,
    arrayType,
    tabNameExpr,
    tabsWrap,
    hasAddBtnInRow,
    hasToolbar,
    headers,
    itemsAreObjectType,
    itemsUiSchema,
    onEmptyMessage,
    positionButtons,
    props,
    rowClassNames,
  });

  return {
    $id,
    layout,
    filterRowJnx,
    formContext,
    hasAddBtn,
    hasAddBtnInRow,
    hasToolbar,
    headerOnEmpty,
    hideHeader,
    searchable,
    uiSectionType,
    headers,
    loading: loadingOptions,
    errorLoading: errorLoadingOptions,
    itemsAreObjectType,
    itemsUiSchema,
    jnxOnAddAction,
    objectItemsSchema,
    onEmptyMessage,
    panels,
    perRowClassNamesIf,
    perRowShowColumnsIf,
    uiDescription,
    disabled,
    readonlyRowIfJnx,
    rootFormData,
    rowClassNames,
    expandable,
    expanded,
    toggleExpand,
    hideTitle,
    additionalButtons,
    selectedItem,
    setSelectedItem,
    asCard,
    arrayLookupCache: arrayLookupCacheContextValue,
    itemLookupEntries,
  };
}

export default ArrayFieldTemplate;
